"use client";

import { useEffect, useState } from "react";
import clsx from "clsx";
import type { EventLogState } from "@/lib/sse/useSSE";

/**
 * EventLogOverlay — a slide-in side panel that lists the last 80 SSE
 * events with their type, occurredAt, actor, and payload preview.
 *
 * Power-user mode for technical audiences who ask "what event drove
 * that?". Toggled by `J` (for JSON); Esc dismisses. Hidden by default
 * so it never appears in front of the audience accidentally.
 *
 * Each row is click-to-expand: collapsed shows type + actor + a payload
 * one-liner; expanded reveals the full event JSON. Useful both during
 * stage Q&A and for engineering inspection during rehearsal.
 */
export function EventLogOverlay({ ev }: { ev: EventLogState }) {
  const [open, setOpen] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Keyboard binding — `J` toggles, Esc closes. Skip when focus is in an
  // input so a PIN field doesn't trigger.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable)) return;
      if (e.key === "j" || e.key === "J") {
        e.preventDefault();
        setOpen((v) => !v);
      } else if (e.key === "Escape" && open) {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  if (!open) return null;

  // Show the most recent 80 events, newest at the top.
  const events = ev.events.slice(-80).reverse();

  return (
    <div
      role="dialog"
      aria-label="Event log inspector"
      className="fixed inset-y-0 end-0 z-50 flex w-[min(560px,92vw)] flex-col bg-orbitra-ink/95 shadow-[0_0_60px_var(--state-financed-glow)] backdrop-blur-md"
    >
      <div className="flex items-center justify-between border-b border-state-financed/30 px-4 py-3">
        <div>
          <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-state-financed">
            Event log inspector · last {events.length}
          </p>
          <p className="mt-0.5 font-display text-sm font-semibold text-orbitra-paper">
            What fired this screen
          </p>
        </div>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="rounded-full border border-orbitra-muted/40 px-3 py-0.5 font-mono text-[10px] uppercase tracking-wider text-orbitra-muted hover:border-state-financed/40 hover:text-state-financed"
        >
          Esc · J
        </button>
      </div>

      <div className="flex-1 overflow-y-auto">
        <ul className="flex flex-col">
          {events.map((e) => {
            const isExpanded = expandedId === e.eventId;
            const payloadPreview = previewPayload(e.payload);
            return (
              <li
                key={e.eventId}
                className={clsx(
                  "border-b border-orbitra-muted/15 px-4 py-2 text-[11px] transition-colors",
                  isExpanded
                    ? "bg-state-financed/8"
                    : "hover:bg-state-financed/4 cursor-pointer",
                )}
                onClick={() => setExpandedId(isExpanded ? null : e.eventId)}
              >
                <div className="flex items-baseline justify-between gap-2">
                  <span className="font-mono text-state-financed">{e.eventType}</span>
                  <span className="font-mono text-[9px] text-orbitra-muted">
                    {new Date(e.occurredAt).toLocaleTimeString(undefined, { hour12: false })}
                  </span>
                </div>
                <p className="mt-0.5 text-[10px] text-orbitra-muted">
                  actor · <span className="text-orbitra-paper">{e.actor.id}</span>
                </p>
                {!isExpanded && payloadPreview && (
                  <p className="mt-0.5 truncate font-mono text-[10px] text-orbitra-teal-soft">
                    {payloadPreview}
                  </p>
                )}
                {isExpanded && (
                  <pre className="mt-2 max-h-72 overflow-auto rounded border border-state-financed/20 bg-orbitra-ink/60 p-2 font-mono text-[10px] leading-relaxed text-orbitra-paper">
                    {JSON.stringify(e, null, 2)}
                  </pre>
                )}
              </li>
            );
          })}
          {events.length === 0 && (
            <li className="px-4 py-6 text-center text-[11px] text-orbitra-muted">
              No events yet. Start a scenario to populate the log.
            </li>
          )}
        </ul>
      </div>

      <div className="border-t border-state-financed/30 px-4 py-2 font-mono text-[9px] uppercase tracking-wider text-orbitra-muted">
        Press <kbd className="rounded border border-state-financed/40 bg-state-financed/10 px-1 py-0.5 text-state-financed">J</kbd> to toggle · <kbd className="rounded border border-orbitra-muted/40 px-1 py-0.5">Esc</kbd> closes
      </div>
    </div>
  );
}

function previewPayload(payload: Record<string, unknown>): string {
  const keys = Object.keys(payload);
  if (keys.length === 0) return "";
  const head = keys.slice(0, 3).map((k) => {
    const v = payload[k];
    if (typeof v === "string") return `${k}: ${v.slice(0, 32)}${v.length > 32 ? "…" : ""}`;
    if (typeof v === "number" || typeof v === "boolean") return `${k}: ${v}`;
    return `${k}: …`;
  }).join(" · ");
  return head + (keys.length > 3 ? " · …" : "");
}
